import React, { useState } from 'react';
import { borrowingApi } from '../services/api';
import '../styles/Modal.css';

function GenerateBorrowingAnalysisModal({ entity, onClose, onComplete }) {
  const [reportingDate, setReportingDate] = useState('');
  const [includeRepaymentSchedule, setIncludeRepaymentSchedule] = useState(true);
  const [includeTBReconciliation, setIncludeTBReconciliation] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [outputBlob, setOutputBlob] = useState(null);

  const handleCalculate = async () => {
    if (!reportingDate) {
      setError('Please enter a reporting date');
      return;
    }

    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      const data = await borrowingApi.calculateBorrowingAnalysis(
        entity.name,
        reportingDate,
        includeRepaymentSchedule,
        includeTBReconciliation
      );
      setResult(data);

      // Fetch the output file for immediate download option
      const blob = await borrowingApi.downloadBorrowingOutput(
        entity.name,
        reportingDate,
        data.timestamp
      );
      setOutputBlob(blob);
    } catch (err) {
      setError(err.message || 'Failed to calculate borrowing analysis');
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = () => {
    if (!outputBlob || !result) return;

    const url = window.URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `borrowing_analysis_${entity.fileSlug}_${result.reporting_date}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleClose = () => {
    if (result && onComplete) {
      onComplete();
    } else {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate Borrowing & Finance Costs Analysis</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {result && (
            <div className="alert alert-success">
              Successfully generated borrowing analysis!
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li>Reporting Date: {result.reporting_date}</li>
                <li>Total Borrowings: ₹ {result.summary.grand_total.borrowings.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
                <li>Total Interest: ₹ {result.summary.grand_total.interest.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
                <li>Total Forex Gain/Loss: ₹ {result.summary.grand_total.forex_gl.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
              </ul>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                <strong>Breakdown by Loan Type:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>Cash Credit: ₹ {result.summary.cash_credit.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                  <li>Buyer's Credit: ₹ {result.summary.buyers_credit.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                  <li>FCNR Loans: ₹ {result.summary.fcnr.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                  <li>Term Loans: ₹ {result.summary.term_loan.principal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                  <li>Vehicle Loans: ₹ {result.summary.vehicle_loan.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                  <li>Related Party: ₹ {result.summary.related_party.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</li>
                </ul>
              </div>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                <strong>Schedule III Classification:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>Current Portion (≤ 12 months): ₹ {result.classification.current.total.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                  <li>Non-Current Portion (&gt; 12 months): ₹ {result.classification.non_current.total.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                </ul>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                style={{ marginTop: '15px' }}
              >
                Download Analysis
              </button>
            </div>
          )}

          {!result && (
            <>
              <p>Calculate borrowing & finance costs analysis for {entity.uiName}</p>

              <div className="form-group">
                <label htmlFor="reporting-date">Reporting Date</label>
                <input
                  type="date"
                  id="reporting-date"
                  className="form-control"
                  value={reportingDate}
                  onChange={(e) => setReportingDate(e.target.value)}
                  disabled={calculating}
                />
                <small className="form-text">
                  Borrowings will be classified as Current or Non-Current based on maturity date
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeRepaymentSchedule}
                    onChange={(e) => setIncludeRepaymentSchedule(e.target.checked)}
                    disabled={calculating}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Include Repayment Schedule</span>
                </label>
                <small className="form-text">
                  Generate detailed repayment schedule with principal/interest split
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeTBReconciliation}
                    onChange={(e) => setIncludeTBReconciliation(e.target.checked)}
                    disabled={calculating}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Include TB Reconciliation</span>
                </label>
                <small className="form-text">
                  Reconcile borrowing balances with Trial Balance GL accounts
                </small>
              </div>

              <div className="alert alert-info" style={{ marginTop: '15px' }}>
                <strong>Output includes:</strong>
                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  <li>Borrowing Register with maturity analysis</li>
                  <li>Lender-wise summary</li>
                  <li>Schedule III disclosure format (IndAS compliant)</li>
                  <li>Finance Cost Analysis</li>
                  <li>Current vs Non-Current classification</li>
                  {includeRepaymentSchedule && <li>Repayment schedule</li>}
                  {includeTBReconciliation && <li>Trial Balance reconciliation</li>}
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleCalculate}
              disabled={!reportingDate || calculating}
            >
              {calculating ? 'Calculating...' : 'Generate'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateBorrowingAnalysisModal;
